package com.islamicmedia.shared.domain.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The in-app cancellation flow: the survey, the save offers, and the copy for all of it.
 *
 * ADMIN-EDITABLE. This used to be two hardcoded enums, which meant rewording a headline was a
 * release on two app stores, and the server kept its own duplicate of the same rules. It is now
 * one JSON row (`cancel_flow_config`), served by GET /api/cancel-flow, with [DEFAULT] as the
 * fallback whenever that fetch fails or nobody has edited it yet. [DEFAULT] is exactly the flow as
 * it originally shipped, so an unconfigured environment behaves as before.
 *
 * WHY THE COPY IS STILL SHARED: web (Compose HTML) and mobile (Material 3) cannot share a
 * composable, so anything written at the call site gets written twice and drifts. Each platform
 * decides only how to LAY THESE OUT.
 */
@Serializable
data class CancelFlowConfig(
    val reasons: List<CancelReasonOption> = DEFAULT_REASONS,
    /**
     * The save offers, in escalation order. A reason may promote one of them to the front (see
     * [CancelReasonOption.leadOffer]); everything else keeps this order, so someone who declines
     * every offer always ends on the most generous one rather than on whatever was left.
     */
    val offers: List<RetentionOfferConfig> = DEFAULT_OFFERS,
    /** Informational bump-on-save marker, like pricing_config's. */
    val version: Long? = null,
) {
    val activeReasons: List<CancelReasonOption> get() = reasons.filter { it.enabled }
    val activeOffers: List<RetentionOfferConfig> get() = offers.filter { it.enabled }

    fun reason(key: String?): CancelReasonOption? = reasons.firstOrNull { it.key == key }
    fun offer(key: String?): RetentionOfferConfig? = offers.firstOrNull { it.key == key }

    /**
     * Which offers to make, in order, for a given survey answer.
     *
     * Only the FIRST position moves. Promoting the matched offer and leaving the rest in
     * escalation order is what guarantees the flow still ends on the biggest concession.
     */
    fun offerOrderFor(reasonKey: String?): List<RetentionOfferConfig> {
        val ordered = activeOffers
        val lead = reason(reasonKey)?.leadOffer?.let { key -> ordered.firstOrNull { it.key == key } }
            ?: return ordered
        return listOf(lead) + ordered.filterNot { it.key == lead.key }
    }

    companion object {
        val DEFAULT_REASONS = listOf(
            CancelReasonOption("too_expensive", "It's too expensive", leadOffer = "discount_50"),
            CancelReasonOption("not_using", "I'm not using it enough", leadOffer = "pause_1m"),
            CancelReasonOption("technical", "I ran into technical problems"),
            CancelReasonOption("missing_features", "It's missing something I need"),
            CancelReasonOption("other", "Something else", allowNote = true),
        )

        val DEFAULT_OFFERS = listOf(
            RetentionOfferConfig(
                key = "discount_50",
                kind = OfferKind.DISCOUNT,
                percentOff = 50,
                months = 1,
                headline = "Stay for half the price",
                body = "Take 50% off your next month. No lock-in, no new commitment. You can still " +
                    "cancel any time, and your price goes back to normal the month after.",
                acceptLabel = "Yes, give me 50% off",
                confirmation = "Done. Your next payment is half price.",
            ),
            RetentionOfferConfig(
                key = "pause_1m",
                kind = OfferKind.PAUSE,
                months = 1,
                headline = "Not ready? Pause instead",
                body = "Put your subscription on hold for a month. You won't be charged, and your " +
                    "access comes back on its own. Nothing to remember, nothing to re-do.",
                acceptLabel = "Pause for a month",
                confirmation = "Your subscription is paused. We'll be here when you're back.",
            ),
            RetentionOfferConfig(
                key = "free_1m",
                kind = OfferKind.FREE,
                months = 1,
                headline = "Have a month on us",
                body = "One full month free, with everything you have now. No strings, no " +
                    "commitment. If it still isn't for you next month, cancel then and pay " +
                    "nothing extra.",
                acceptLabel = "Yes, I'll take a free month",
                confirmation = "Enjoy. Your next month is on us.",
            ),
        )

        val DEFAULT = CancelFlowConfig()
    }
}

/**
 * One survey answer.
 *
 * [key] is the wire format and the analytics dimension, written into `cancellation_flows.reason`.
 * Reword [label] freely; changing [key] splits the metric in two and orphans the history.
 */
@Serializable
data class CancelReasonOption(
    val key: String,
    val label: String,
    val enabled: Boolean = true,
    /**
     * Show a free-text box when this answer is picked.
     *
     * Off by default on purpose: an always-visible text box turns a one-tap question into a form,
     * and the answer most worth having is the tap, not the essay.
     */
    @SerialName("allow_note") val allowNote: Boolean = false,
    /** Offer key to lead with for this answer. Null keeps the standard escalation order. */
    @SerialName("lead_offer") val leadOffer: String? = null,
)

/**
 * What the server actually knows how to DO for a save offer.
 *
 * This is the line between config and code. An admin can add, remove, reword, reorder and
 * re-parameterise offers all day, but each one must be one of these kinds, because each kind maps
 * to a specific Stripe operation. A fourth kind is a code change, and keeping that explicit is
 * what stops the config from promising something the server cannot perform.
 */
object OfferKind {
    /** A percent-off coupon on the next invoice (or the next N invoices). */
    const val DISCOUNT = "discount"

    /** Skip N months of billing. Access LAPSES on the date already paid through. */
    const val PAUSE = "pause"

    /** Skip N months of billing. Access CONTINUES throughout. */
    const val FREE = "free"

    val ALL = listOf(DISCOUNT, PAUSE, FREE)

    /**
     * Kinds whose promise only holds on a MONTHLY subscription, so the server withholds them from
     * annual members no matter what the config says.
     *
     * A percent-off coupon applies to the next INVOICE, so "half off your next month" on an annual
     * plan is half off a year. A pause skips the next billing cycle, which on an annual plan may
     * be ten months away, so it would mean nothing for most of a year. Only [FREE] reads correctly
     * on any interval, because pushing the renewal date out by a month is the same promise either
     * way.
     */
    val MONTHLY_ONLY = setOf(DISCOUNT, PAUSE)
}

/**
 * One save offer: what it does, and what it says while doing it.
 *
 * [key] is written into the `retention_offers` ledger and is what the once-per-year eligibility
 * check matches on. Renaming it silently re-grants the offer to everyone who already took it.
 */
@Serializable
data class RetentionOfferConfig(
    val key: String,
    /** One of [OfferKind]. An unrecognised kind is skipped rather than guessed at. */
    val kind: String,
    val headline: String,
    val body: String,
    @SerialName("accept_label") val acceptLabel: String,
    /** What to say once it has been applied. */
    val confirmation: String,
    val enabled: Boolean = true,
    /** [OfferKind.DISCOUNT] only. Ignored by the other kinds. */
    @SerialName("percent_off") val percentOff: Int = 0,
    /**
     * For [OfferKind.PAUSE] and [OfferKind.FREE], how many months to skip. For
     * [OfferKind.DISCOUNT], how many invoices the coupon applies to (1 = once only).
     */
    val months: Int = 1,
) {
    val isKnownKind: Boolean get() = kind in OfferKind.ALL
    val monthlyOnly: Boolean get() = kind in OfferKind.MONTHLY_ONLY
}

/**
 * What the server says about one cancellation attempt: the flow row to report steps against, and
 * which offers this person may actually be shown.
 *
 * [offers] is already ordered and already filtered. An offer someone took six months ago, or one
 * that is unsafe for their billing interval, is simply absent, so the flow silently gets shorter
 * rather than presenting something that would be refused on accept.
 */
data class CancelFlowStart(
    val flowId: String,
    val offers: List<RetentionOfferConfig>,
    /** When their access ends if they go through with it. Null when the date is unknown. */
    val periodEndIso: String?,
    /**
     * Set only for a member still inside the first month of a referral: cancelling now would
     * take away the free month their referral gave them. It is the date their next bill will be if
     * they stay (the warning's date: ReferralCopy.cancelWarning). [periodEndIso] then already has the
     * free month taken off. Null for everyone else, and from a server that doesn't send it.
     */
    val referralFreeMonthUntilIso: String? = null,
)
